// Сервис для работы с заказами в Firestore
import {
    collection,
    addDoc,
    getDocs,
    query,
    orderBy,
    limit,
    serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

const COLLECTION_NAME = 'orderssite';

/**
 * Сохраняет новый заказ в Firestore
 * @param {Object} orderData - Данные заказа
 * @param {string} orderData.name - Имя клиента
 * @param {string} orderData.phone - Телефон клиента
 * @param {string} orderData.source - Источник заказа (например, 'website', 'chatbot')
 * @param {string} orderData.status - Статус заказа (по умолчанию 'new')
 * @returns {Promise<Object>} - Результат сохранения
 */
export const saveOrder = async (orderData) => {
    try {
        // Сохранение заказа в Firestore

        // Подготавливаем данные для сохранения
        const orderToSave = {
            name: orderData.name,
            phone: orderData.phone,
            source: orderData.source || 'website',
            status: orderData.status || 'new',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            // Дополнительные поля
            language: orderData.language || 'ru',
            userAgent: orderData.userAgent || navigator.userAgent,
            referrer: orderData.referrer || document.referrer || 'direct',
            timestamp: orderData.timestamp || new Date().toISOString(),
            // Данные о товаре (если есть)
            productName: orderData.productName || '',
            productPrice: orderData.productPrice || 0,
            productSize: orderData.productSize || '',
            productFeatures: orderData.productFeatures || [],
            productDescription: orderData.productDescription || '',
            // IP адрес (если нужно, можно получить через API)
            ipAddress: orderData.ipAddress || null
        };

        // Сохраняем в коллекцию orderssite
        const docRef = await addDoc(collection(db, COLLECTION_NAME), orderToSave);

        // Заказ успешно сохранен

        return {
            success: true,
            orderId: docRef.id,
            message: 'Заказ успешно сохранен'
        };

    } catch (error) {
        throw new Error(`Не удалось сохранить заказ: ${error.message}`);
    }
};

/**
 * Получает все заказы из Firestore
 * @param {number} limitCount - Количество заказов для получения
 * @returns {Promise<Array>} - Массив заказов
 */
export const getOrders = async (limitCount = 50) => {
    try {
        // Получение заказов из Firestore

        const ordersRef = collection(db, COLLECTION_NAME);
        const q = query(ordersRef, orderBy('createdAt', 'desc'), limit(limitCount));

        const querySnapshot = await getDocs(q);
        const orders = [];

        querySnapshot.forEach((doc) => {
            orders.push({
                id: doc.id,
                ...doc.data()
            });
        });

        // Получено заказов
        return orders;

    } catch (error) {
        throw new Error(`Не удалось получить заказы: ${error.message}`);
    }
};

/**
 * Проверяет существование коллекции orderssite
 * @returns {Promise<boolean>} - true если коллекция существует
 */
export const checkCollectionExists = async () => {
    try {
        const ordersRef = collection(db, COLLECTION_NAME);
        const q = query(ordersRef, limit(1));
        const querySnapshot = await getDocs(q);

        return !querySnapshot.empty;
    } catch (error) {
        return false;
    }
};

/**
 * Получает статистику заказов
 * @returns {Promise<Object>} - Статистика заказов
 */
export const getOrdersStats = async () => {
    try {
        const orders = await getOrders(1000); // Получаем больше заказов для статистики

        const stats = {
            total: orders.length,
            byStatus: {},
            bySource: {},
            byLanguage: {},
            today: 0,
            thisWeek: 0,
            thisMonth: 0
        };

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        orders.forEach(order => {
            // Статистика по статусам
            stats.byStatus[order.status] = (stats.byStatus[order.status] || 0) + 1;

            // Статистика по источникам
            stats.bySource[order.source] = (stats.bySource[order.source] || 0) + 1;

            // Статистика по языкам
            stats.byLanguage[order.language] = (stats.byLanguage[order.language] || 0) + 1;

            // Временная статистика
            if (order.createdAt) {
                const orderDate = order.createdAt.toDate();
                if (orderDate >= today) stats.today++;
                if (orderDate >= weekAgo) stats.thisWeek++;
                if (orderDate >= monthAgo) stats.thisMonth++;
            }
        });

        return stats;

    } catch (error) {
        throw new Error(`Не удалось получить статистику: ${error.message}`);
    }
};
